import type { ReactNode } from "react";
import { ChevronDown, type LucideIcon } from "lucide-react";

import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";

interface CollapsibleRailSectionProps {
  title: string;
  icon?: LucideIcon;
  badge?: number;
  /** When set with `badge`, e.g. "3 active" beside the title. */
  badgeSuffix?: string;
  expanded: boolean;
  onToggle: () => void;
  /** When true, expanded body fills remaining card height (for Kai chat). */
  fill?: boolean;
  /** Size to content instead of clipping to a nested 1fr row (mobile sheet). */
  flow?: boolean;
  /**
   * Accordion: body opens below a pinned title. The header never recenters or
   * jumps when siblings grow — only the body height changes.
   */
  accordion?: boolean;
  /** With `fill`: scroll the body inline instead of clipping (non-Kai cards). */
  scroll?: boolean;
  /** Icon buttons rendered between the title and the expand chevron. */
  actions?: ReactNode;
  className?: string;
  contentClassName?: string;
  children: React.ReactNode;
}

export function CollapsibleRailSection({
  title,
  icon: Icon,
  badge,
  badgeSuffix,
  expanded,
  onToggle,
  fill = false,
  flow = false,
  accordion = false,
  scroll = false,
  actions,
  className,
  contentClassName,
  children,
}: CollapsibleRailSectionProps) {
  return (
    <section
      className={cn(
        "rail-section",
        expanded && "rail-section--expanded",
        fill && !accordion && "rail-section--fill",
        fill && !accordion && scroll && "rail-section--scroll",
        flow && "rail-section--flow",
        accordion && "rail-section--accordion",
        className,
      )}
    >
      <div className="rail-section-header flex shrink-0 items-center gap-0.5">
        <button
          type="button"
          onClick={onToggle}
          className="flex min-w-0 flex-1 items-center gap-2 rounded-lg px-1 py-1.5 text-left transition-colors hover:bg-accent/30"
          aria-expanded={expanded}
        >
          {Icon && <Icon className="size-4 shrink-0 text-primary" />}
          <span className="flex min-w-0 flex-1 items-center gap-1.5">
            <h3 className="min-w-0 truncate text-sm font-semibold">{title}</h3>
            {badge != null && (
              <Badge
                variant={badgeSuffix ? "secondary" : "info"}
                className="shrink-0 px-1.5 py-0 text-[10px] tabular-nums"
              >
                {badgeSuffix ? `${badge} ${badgeSuffix}` : badge}
              </Badge>
            )}
          </span>
        </button>
        {actions}
        <button
          type="button"
          onClick={onToggle}
          className="flex size-7 shrink-0 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-accent/30 hover:text-foreground"
          aria-expanded={expanded}
          aria-label={expanded ? `Collapse ${title}` : `Expand ${title}`}
        >
          <ChevronDown
            className={cn(
              "size-4 transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]",
              expanded && "rotate-180",
            )}
          />
        </button>
      </div>

      <div className="rail-section-body">
        <div className={cn("rail-section-inner", contentClassName)}>
          {children}
        </div>
      </div>
    </section>
  );
}

/** Animated mount wrapper for notifications — drops in from above the rail. */
export function RailNotificationsMount({
  open,
  animating,
  fill = false,
  className,
  children,
}: {
  open: boolean;
  animating: boolean;
  fill?: boolean;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        "rail-notifications-mount",
        open && "rail-notifications-mount--open",
        fill && "rail-notifications-mount--fill",
        animating && "rail-notifications-mount--animating",
        className,
      )}
    >
      <div className="rail-notifications-mount__inner">{children}</div>
    </div>
  );
}

/** Animated mount wrapper for the Kai card enter / exit. */
export function RailKaiMount({
  open,
  animating,
  className,
  children,
}: {
  open: boolean;
  animating: boolean;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        "rail-kai-mount",
        open && "rail-kai-mount--open",
        animating && "rail-kai-mount--animating",
        className,
      )}
    >
      {children}
    </div>
  );
}
